// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { useAuthContext } from "@asgardeo/auth-react";
import { ChecklistRtl, Dns, Inventory, PendingActions, Today } from '@mui/icons-material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PersonIcon from '@mui/icons-material/Person';
import { Button } from '@mui/material';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import DOMPurify from 'dompurify';
import 'quill/dist/quill.snow.css';
import { useContext, useEffect, useState } from 'react';
import { Store } from 'react-notifications-component';
import { useQuill } from 'react-quilljs';
import { useNavigate, useParams } from 'react-router-dom';
import { AttachmentBox } from '../components/AttachmentBox';
import { PermissionContext } from '../components/Authorize';
import { CaseBox } from '../components/CaseBox';
import LinearLoading from '../components/LinearLoading';
import CircularLoading from '../components/Loading';
import PathView from '../components/PathView';
import { CASE_CLOSED_STATE } from "../constants/constants";
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import { useGetApi } from '../data/hooks/api';
import * as Types from "../data/utils/types";
import '../styles/CaseView.css';
import { ErrorComponent } from './ErrorPage';
import { ItemNotFoundComponent } from './ItemNotFound';

type RouteParams = {
   caseId: string;
}

export const CaseView = () => {
   const { caseId: id } = useParams<RouteParams>();
   const caseId = id ? DOMPurify.sanitize(id) : '';
   const [worknoteText, setWorknoteText] = useState('<p><br></p>');
   const [worknoteResponse, setWorknoteResponse] = useState();
   const { httpRequest } = useAuthContext();
   const [showAddWorkNotes, setShowAddWorkNotes] = useState(false);
   const [showLoading, setShowLoading] = useState(false);
   const authInfo = useContext(PermissionContext);
   const [isStateClosed, setIsStateClosed] = useState(false);
   const navigate = useNavigate();
   const options = {
      modules: {
         toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
         ],
      },
      theme: 'snow',
      formats: ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet'],
   };
   const { quill, quillRef } = useQuill({ ...options });

   const apiUrl = RESOURCE_SERVER_URLS[0] + `/cases/${caseId}`;
   const { data, loading, error, getApiData } = useGetApi<Types.caseDetails>({ url: apiUrl, headers: { "accept": "application/json" } });

   useEffect(() => {
      getApiData();
   }, []);

   useEffect(() => {
      if (data?.state === CASE_CLOSED_STATE) {
         setIsStateClosed(true);
      }
   }, [data]);

   useEffect(() => {
      if (quill) {
         quill.on('text-change', () => {
            setWorknoteText(quillRef.current.firstChild.innerHTML);
         });
      }
   }, [quill]);

   const addWorkNote = async () => {

      if (worknoteText == '<p><br></p>') {
         Store.addNotification({
            title: "error!",
            message: "worknote cannot be empty.",
            type: "warning",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
               duration: 5000,
               onScreen: true
            }
         });
         return;
      }
      setShowAddWorkNotes(prev => !prev);
      const intertWorkNotes = RESOURCE_SERVER_URLS[0] + `/cases/${caseId}/worknote`;
      const sanitizedInput = DOMPurify.sanitize(worknoteText);
      if (sanitizedInput.length > 0) {
         try {
            setShowLoading(true);
            const response = await httpRequest({
               url: intertWorkNotes,
               method: 'POST',
               headers: { "accept": "application/json" },
               data: { "worknote": sanitizedInput },
            });
            if (response.status === 201) {
               Store.addNotification({
                  title: "success!",
                  message: "worknote added successfully.",
                  type: "success",
                  insert: "top",
                  container: "top-right",
                  animationIn: ["animate__animated", "animate__fadeIn"],
                  animationOut: ["animate__animated", "animate__fadeOut"],
                  dismiss: {
                     duration: 5000,
                     onScreen: true
                  }
               });
            }
            setWorknoteResponse(response.data);
            setShowLoading(false);
            if (quill) {
               quill.clipboard.dangerouslyPasteHTML('<p><br></p>');
            }
         } catch (error: any) {
            console.error(error.response.data);
            Store.addNotification({
               title: "failed!",
               message: "worknote submission unsuccessful.",
               type: "danger",
               insert: "top",
               container: "top-right",
               animationIn: ["animate__animated", "animate__fadeIn"],
               animationOut: ["animate__animated", "animate__fadeOut"],
               dismiss: {
                  duration: 5000,
                  onScreen: true
               }
            });
            setShowLoading(false);
         }
      }
   };

   function toggleWorkNotes() {
      setShowAddWorkNotes(prev => !prev);
   }

   const priorityChipColor = {
      backgroundColor: data?.priority === 'Critical (P1)' ? '#bf2600' : data?.priority === 'High (P2)'
         ? '#ff8b00' : data?.priority === 'Medium (P3)' ? '#ffc400' : '#ff7300',
      filter: " drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))"
   }

   const statusChipColor = {
      backgroundColor: data?.state === 'Open' ? '#0052cc' : data?.state === 'Work In Progress' ? '#008000'
         : data?.state === 'Awaiting Info' ? '#00bfa5' : data?.state === 'Solution Proposed' ? '#4caf50' : '#8993a4',
      color: '#fff',
      filter: " drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))"
   }
   
   const CaseHeader = () => {
      return (
         <>
            {data && (
               <div className="header-box" >
                  <h3>{data.shortDescription}</h3>
                  <b><p style={{ color: '#ff7300' }}>{data.number}&nbsp;&nbsp;{data.caseId}</p></b>
                  <Stack
                     sx={{
                        width: 'fit-content',
                        margin: "0 auto",
                        padding: "10px 0px 10px 0px",
                        borderTop: "1px solid #D2C9C9",
                        borderBottom: "1px solid #D2C9C9"
                     }}
                     direction="row" spacing={1}
                  >
                     {data.caseType &&
                        <Tooltip title="Case Type">
                           <Chip sx={{ filter: " drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))" }} label={data.caseType} color="secondary" />
                        </Tooltip>}
                     {data.priority &&
                        <Tooltip title="Priority">
                           <Chip sx={priorityChipColor} label={data.priority} color="error" />
                        </Tooltip>}
                     <Tooltip title="Status">
                        <Chip sx={statusChipColor} label={data.state} />
                     </Tooltip>
                  </Stack>
                  <div
                     style={{
                        display: "flex",
                        justifyContent: "space-between",
                        margin: "50px 20px 10px 20px",

                     }}
                  >
                     <div className='header-item-left'>
                        <p >
                           <PersonIcon fontSize="small" style={{ color: "#212A30" }} />
                           <span style={{ marginLeft: "10px" }}> Assigned to : </span>
                           <span style={{ marginLeft: "10px" }}>{data.assignedTo}</span>
                        </p>
                        <p >
                           <Today fontSize="small" style={{ color: "#212A30" }} />
                           <span style={{ marginLeft: "10px" }}> Opened on : </span>
                           <span style={{ marginLeft: "10px" }}> {data.openedAt.split(" ")[0]}</span>
                        </p>
                        <p >
                           <PersonIcon fontSize="small" style={{ color: "#212A30" }} />
                           <span style={{ marginLeft: "10px" }}> Opened By : </span>
                           <span style={{ marginLeft: "10px" }}> {data.openedBy} </span>
                        </p>
                        <p>
                           <Inventory fontSize="small" style={{ color: "#212A30" }} />
                           <span style={{ marginLeft: "10px" }}> Product : </span>
                           <span style={{ marginLeft: "10px" }}>
                              {data.productName ? data.productName : "N/A"}
                           </span>
                        </p>
                     </div>
                     
                     <div className='header-item-right '>
                        <p>
                           <PendingActions fontSize="small" style={{ color: "#212A30" }} />
                           <span style={{ marginLeft: "10px" }}> Last WSO2 Comment : </span>
                           <span style={{ marginLeft: "10px" }}> {data?.lastWSO2CommentTime}  </span>
                        </p>
                        <p>
                           <PendingActions fontSize="small" style={{ color: "#212A30" }} />
                           <span style={{ marginLeft: "10px" }}> Last Customer Comment : </span>
                           <span style={{ marginLeft: "10px" }}> {data?.lastCustomerCommentTime} </span>
                        </p>
                        <p>
                           <Dns fontSize="small" style={{ color: "#212A30" }} />
                           <span style={{ marginLeft: "10px" }}> Deployment Name : </span>
                           <span style={{ marginLeft: "10px" }}> {data?.projectDeploymentName}  </span>
                        </p>
                        <p>
                           <ChecklistRtl fontSize="small" style={{ color: "#212A30" }} />
                           <span style={{ marginLeft: "10px" }}> Deployment Type : </span>
                           <span style={{ marginLeft: "10px" }}> {data?.projectDeploymentType} </span>
                        </p>

                     </div>
                  </div>
               </div>
            )}
         </>
      );
   };
   const MainView = () => {
      return (
         <div className="col-12 col-md-10 col-lg-12 ">
            <div className="card card-sm case-view-box">
               <div className="card-body row no-gutters align-items-center">
                  <CircularLoading openLoading={showLoading} />
                  <PathView
                     accountName={data?.accountName ? data.accountName : data?.accountNumber}
                     projectKey={data?.projectKey ? data.projectKey : data?.projectNumber}
                     accountNumber={data?.accountNumber}
                     projectNumber={data?.projectNumber}
                     caseKey={data?.caseId.split('-')[1]}
                  />
                  <CaseHeader />
                  <div className="container">
                     <div className="row d-flex">
                        <div className="col-12 col-md-10 col-lg-9">
                           {isStateClosed ? (
                              <Tooltip title="Case is closed">
                                 <span>
                                    <Button
                                       variant="contained"
                                       disabled
                                       startIcon={<NoteAddIcon fontSize="small" />}
                                    >
                                       New Work Note
                                    </Button>
                                 </span>
                              </Tooltip>
                           ) : !showAddWorkNotes &&
                              authInfo.isUserAllowedtoAddWorkNotes ? (
                              <button
                                 style={{ display: "flex", verticalAlign: "middle" }}
                                 className="btn btn-outline-primary add-worknote-btn"
                                 onClick={toggleWorkNotes}
                              >
                                 <NoteAddIcon fontSize="small" /> &nbsp;New Work Note
                              </button>
                           ) : !showAddWorkNotes &&
                              !authInfo.isUserAllowedtoAddWorkNotes ? (
                              <Tooltip title="You don't have the permission">
                                 <span>
                                    <Button
                                       variant="contained"
                                       disabled
                                       startIcon={<NoteAddIcon fontSize="small" />}
                                    >
                                       New Work Note
                                    </Button>
                                 </span>
                              </Tooltip>
                           ) : null}

                           <div style={{ display: showAddWorkNotes ? "block" : "none" }}>
                              <label className='p-1'>Work Notes : </label>
                              <div style={{ height: '100px' }} className='worknoteInput' ref={quillRef}></div>
                              <div className='pt-3'>
                                 <button className="btn btn-outline-primary add-worknote-btn" onClick={addWorkNote} type="button" >Add Note</button>
                                 <button className="btn btn-outline-primary cancel-worknote-btn" onClick={toggleWorkNotes} type="button" >Cancel</button>
                              </div>
                           </div>
                           <CaseBox caseId={caseId} worknoteRsp={worknoteResponse} />
                        </div>

                        <div className="col-lg-3 p-4">
                           <div className="card">
                              <div className="card-body" >
                                 <div className='title'>
                                    <h5>Attachments</h5>
                                 </div>
                                 <div className='pt-2'>
                                    <AttachmentBox caseId={caseId} />
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      )
   }

   return (
      <>
         {
            loading
               ? <LinearLoading />
               : error
                  ? error.status == 404
                     ? <ItemNotFoundComponent />
                     : <ErrorComponent />
                  : data && MainView()
         }
      </>
   );
}
